import { type FC, useState, useEffect, useCallback } from "react";

import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  FormField,
  Tooltip,
  useDisclosure,
  Column,
  Row,
  Text,
  CheckIcon,
  CloseIcon,
  WarningIcon,
  ChevronDownIcon,
  Popover,
  PopoverContent,
} from "@hightouch/htui";

import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useUser } from "src/contexts/user-context";
import { ImpersonationPermissionsModal } from "./header/impersonation-permissions-modal";
import { PopoverTriggerButton } from "src/ui/popover/popover-trigger-button";

// Size for the notch cutouts
const CUTOUT_SIZE = 10;

// Color variant for the impersonation overlay
type ColorVariant = "warning" | "danger";

// SVG corner piece for the decorative cutout where the notch meets the border
// These extend outward from the notch's top corners, creating curved transition to the border
const CornerCutout: FC<{
  position: "left" | "right";
  variant: ColorVariant;
}> = ({ position, variant }) => {
  // Position at top of notch, extending outward to the left/right
  const positions = {
    left: { top: "0", left: `-${CUTOUT_SIZE}px` },
    right: { top: "0", right: `-${CUTOUT_SIZE}px` },
  };

  // The cutouts fill the gap between the notch edge and the top border
  // Left: curve in bottom-right corner (inside edge faces the notch)
  // Right: curve in bottom-left corner (inside edge faces the notch)
  const paths = {
    // Left cutout: fills from (0,0) with curve in bottom-right
    left: `M${CUTOUT_SIZE} 0 L${CUTOUT_SIZE} ${CUTOUT_SIZE} Q${CUTOUT_SIZE} 0 0 0 Z`,
    // Right cutout: fills from (SIZE,0) with curve in bottom-left
    right: `M0 0 L0 ${CUTOUT_SIZE} Q0 0 ${CUTOUT_SIZE} 0 Z`,
  };

  const fillColor =
    variant === "danger"
      ? "var(--chakra-colors-danger-base)"
      : "var(--chakra-colors-orange-400)";

  return (
    <Box
      as="svg"
      width={`${CUTOUT_SIZE}px`}
      height={`${CUTOUT_SIZE}px`}
      viewBox={`0 0 ${CUTOUT_SIZE} ${CUTOUT_SIZE}`}
      fill="none"
      position="absolute"
      {...positions[position]}
    >
      <path d={paths[position]} fill={fillColor} />
    </Box>
  );
};

// Corner radius size - should match the content area's border radius
const CORNER_SIZE = 14;

// SVG corner piece for the outer frame corners - fills the gap between
// the rectangular border and the rounded content area
const FrameCorner: FC<{
  position: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  variant: ColorVariant;
}> = ({ position, variant }) => {
  const positions = {
    "top-left": { top: "0", left: "0" },
    "top-right": { top: "0", right: "0" },
    "bottom-right": { bottom: "0", right: "0" },
    "bottom-left": { bottom: "0", left: "0" },
  };

  // Each corner has its own path - the curve should bulge toward the corner
  // (away from the content area) to fill the gap
  const paths = {
    // Top-left: right angle at (0,0), curve from (SIZE,0) to (0,SIZE) bulging toward (0,0)
    "top-left": `M0 0 L${CORNER_SIZE} 0 Q0 0 0 ${CORNER_SIZE} Z`,
    // Top-right: right angle at (SIZE,0), curve from (0,0) to (SIZE,SIZE) bulging toward (SIZE,0)
    "top-right": `M${CORNER_SIZE} 0 L${CORNER_SIZE} ${CORNER_SIZE} Q${CORNER_SIZE} 0 0 0 Z`,
    // Bottom-right: right angle at (SIZE,SIZE), curve from (SIZE,0) to (0,SIZE) bulging toward (SIZE,SIZE)
    "bottom-right": `M${CORNER_SIZE} ${CORNER_SIZE} L0 ${CORNER_SIZE} Q${CORNER_SIZE} ${CORNER_SIZE} ${CORNER_SIZE} 0 Z`,
    // Bottom-left: right angle at (0,SIZE), curve from (0,0) to (SIZE,SIZE) bulging toward (0,SIZE)
    "bottom-left": `M0 ${CORNER_SIZE} L0 0 Q0 ${CORNER_SIZE} ${CORNER_SIZE} ${CORNER_SIZE} Z`,
  };

  const fillColor =
    variant === "danger"
      ? "var(--chakra-colors-danger-base)"
      : "var(--chakra-colors-orange-400)";

  return (
    <Box
      as="svg"
      width={`${CORNER_SIZE}px`}
      height={`${CORNER_SIZE}px`}
      viewBox={`0 0 ${CORNER_SIZE} ${CORNER_SIZE}`}
      fill="none"
      position="absolute"
      {...positions[position]}
    >
      <path d={paths[position]} fill={fillColor} />
    </Box>
  );
};

// Duration to hide banner when keyboard shortcut is pressed
const HIDE_DURATION_MS = 5000;

// Styled keyboard key for shortcut hints
const KeyboardKey: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    as="kbd"
    boxSize="20px"
    display="flex"
    alignItems="center"
    justifyContent="center"
    bg="base.background"
    borderRadius="sm"
    fontSize="xs"
    color="text.tertiary"
    fontFamily="body"
  >
    {children}
  </Box>
);

export const ImpersonationOverlay: FC = () => {
  const { user, workspace } = useUser();
  const [isHidden, setIsHidden] = useState(false);

  // Check if impersonating user has workspace admin permissions
  const { isPermitted: hasWorkspaceAdminPermission } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_update" },
  });

  // Use danger variant when impersonating with workspace admin permissions
  const colorVariant: ColorVariant = hasWorkspaceAdminPermission
    ? "danger"
    : "warning";

  const {
    isOpen: permissionModalOpen,
    onOpen: openPermissionModal,
    onClose: closePermissionModal,
  } = useDisclosure();
  const {
    isOpen: workspaceInfoOpen,
    onOpen: openWorkspaceInfo,
    onClose: closeWorkspaceInfo,
  } = useDisclosure();

  // Hightouch staff can edit their impersonation permissions.
  const canUpgrade = user?.can_impersonate === true;

  // Keyboard shortcut: ⌘⇧ to temporarily hide the banner (works regardless of key order)
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const isShiftThenCmd = event.key === "Meta" && event.shiftKey;
    const isCmdThenShift = event.key === "Shift" && event.metaKey;
    if (isShiftThenCmd || isCmdThenShift) {
      event.preventDefault();
      setIsHidden(true);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Auto-show banner after hiding
  useEffect(() => {
    if (!isHidden) {
      return;
    }
    const timer = setTimeout(() => setIsHidden(false), HIDE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [isHidden]);

  if (!user || !user.not_member_of_current_workspace || isHidden) {
    return null;
  }

  return (
    <>
      {/* Full viewport overlay frame - z-index above theme's highest (tooltip: 1800) */}
      <Box
        position="fixed"
        inset={0}
        pointerEvents="none"
        zIndex={1900}
        border="2px solid"
        borderColor={colorVariant === "danger" ? "danger.base" : "orange.400"}
        overflow="hidden"
      >
        {/* Decorative frame corners */}
        <FrameCorner position="top-left" variant={colorVariant} />
        <FrameCorner position="top-right" variant={colorVariant} />
        <FrameCorner position="bottom-left" variant={colorVariant} />
        <FrameCorner position="bottom-right" variant={colorVariant} />

        {/* Notch container - centered at top */}
        <Box
          display="flex"
          justifyContent="center"
          width="100%"
          position="relative"
        >
          {/* The notch with popover */}
          <Box position="relative" pointerEvents="auto">
            <Popover trigger="hover" placement="bottom" isLazy>
              <PopoverTriggerButton
                aria-label="View impersonation details"
                position="relative"
              >
                {/* Corner cutouts where notch meets frame */}
                <CornerCutout position="left" variant={colorVariant} />
                <CornerCutout position="right" variant={colorVariant} />

                {/* Notch content */}
                <Box
                  className="group"
                  bg={colorVariant === "danger" ? "danger.base" : "orange.400"}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                  height="32px"
                  px={2}
                  py={1}
                  borderBottomRadius="lg"
                >
                  <Box
                    as={WarningIcon}
                    boxSize="16px"
                    color={colorVariant === "danger" ? "white" : "orange.900"}
                  />
                  <Text
                    size="sm"
                    variant="monospace"
                    fontWeight="medium"
                    color={colorVariant === "danger" ? "white" : "orange.900"}
                  >
                    {hasWorkspaceAdminPermission
                      ? "Workspace admin"
                      : user.impersonation_type === "readsupport"
                        ? "Debug access"
                        : "View-only"}
                  </Text>
                  <Box
                    as={ChevronDownIcon}
                    boxSize="16px"
                    color={colorVariant === "danger" ? "white" : "orange.900"}
                    transitionProperty="transform"
                    transitionDuration="fast"
                    transitionTimingFunction="easeOutQuad"
                    _groupHover={{ transform: "translateY(2px)" }}
                  />
                </Box>
              </PopoverTriggerButton>

              <PopoverContent width="auto" boxShadow="md" zIndex={1901}>
                <Column gap={4} p={3}>
                  <Column gap={1} width="100%">
                    <Text fontWeight="medium">
                      You are not a member of workspace{" "}
                      <Text as="strong" fontWeight="semibold">
                        {user.current_workspace_id}
                      </Text>
                    </Text>
                    <Text size="sm" color="text.tertiary">
                      Use caution when assisting customers.
                    </Text>
                  </Column>
                  <Row
                    gap={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    {/* Keyboard shortcut hint */}
                    <Row gap={1} alignItems="center">
                      <KeyboardKey>⌘</KeyboardKey>
                      <KeyboardKey>⇧</KeyboardKey>
                      <Text size="sm" color="text.tertiary">
                        to hide for 5 sec
                      </Text>
                    </Row>
                    <ButtonGroup>
                      <Button
                        size="sm"
                        variant="tertiary"
                        onClick={openWorkspaceInfo}
                      >
                        More info
                      </Button>
                      <Box
                        sx={{
                          "& [data-popper-placement]": {
                            zIndex: 1902,
                          },
                        }}
                      >
                        <Tooltip
                          isDisabled={canUpgrade}
                          message="You have limited permissions in this workspace."
                        >
                          <Button
                            size="sm"
                            variant="warning"
                            isDisabled={!canUpgrade}
                            onClick={openPermissionModal}
                          >
                            Edit permissions
                          </Button>
                        </Tooltip>
                      </Box>
                    </ButtonGroup>
                  </Row>
                </Column>
              </PopoverContent>
            </Popover>
          </Box>
        </Box>
      </Box>

      {/* Modals */}
      <ImpersonationPermissionsModal
        isOpen={permissionModalOpen}
        onClose={closePermissionModal}
      />
      <Dialog
        variant="info"
        isOpen={workspaceInfoOpen}
        title="Workspace information"
        actions={
          <Button variant="primary" onClick={closeWorkspaceInfo}>
            Close
          </Button>
        }
        onClose={closeWorkspaceInfo}
      >
        <Column gap={4}>
          <FormField label="Organization">
            {user?.organization?.name} ({user?.organization?.id})
          </FormField>
          <FormField label="Migrations">
            <Column gap={2}>
              <MigrationInfo
                label="Alerting V2"
                enabled={Boolean(workspace?.alerting_v2_enabled)}
              />
            </Column>
          </FormField>
        </Column>
      </Dialog>
    </>
  );
};

type MigrationInfoProps = {
  label: string;
  enabled?: boolean;
};

const MigrationInfo = ({ label, enabled }: MigrationInfoProps) => (
  <Row gap={2} alignItems="center">
    {enabled ? (
      <CheckIcon color="success.base" />
    ) : (
      <CloseIcon color="danger.base" />
    )}
    <Text>{label}</Text>
  </Row>
);
